import { useState, useMemo } from "react";
import { ContentCard } from "sj-ui-toolkit/src/atlas/components";

import S from "./CatalogResults.module.scss";

// Tab filter types
const FILTER_TABS = { ALL: "all", PATHS: "paths", LESSONS: "lessons" };

const CatalogResults = ({ isLoading, results = [], noResults }) => {
  const [activeTab, setActiveTab] = useState(FILTER_TABS.ALL);

  // Categorize results
  const categorizedResults = useMemo(() => {
    const paths = [];
    const lessons = [];
    results.forEach(item => {
      if (item.contentType === "path") {
        paths.push({ ...item, cardType: "path" });
      } else {
        lessons.push({ ...item, cardType: "lesson" });
      }
    });

    return { paths, lessons };
  }, [results]);

  // Get filtered results based on active tab
  const filteredResults = useMemo(() => {
    switch (activeTab) {
      case FILTER_TABS.PATHS:
        return categorizedResults.paths;
      case FILTER_TABS.LESSONS:
        return categorizedResults.lessons;
      case FILTER_TABS.ALL:
      default:
        return results;
    }
  }, [activeTab, results, categorizedResults]);

  // Count for each tab
  const tabCounts = useMemo(
    () => ({
      all: results.length,
      paths: categorizedResults.paths.length,
      lessons: categorizedResults.lessons.length,
    }),
    [results, categorizedResults]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className={S.resultsContainer}>
        <div className={S.loadingState}>
          <i className="pi pi-spin pi-spinner" />
          <span>Searching catalog...</span>
        </div>
      </div>
    );
  }

  // No results state
  if (noResults) {
    return (
      <div className={S.resultsContainer}>
        <div className={S.noResults}>
          <i className="pi pi-search" />
          <h3>No results found</h3>
          <p>Try adjusting your search terms or filters to find what you're looking for.</p>
        </div>
      </div>
    );
  }

  // Empty results (initial state or cleared)
  if (!results || results.length === 0) {
    return null;
  }

  return (
    <div className={S.resultsContainer}>
      {/* Tabs Header */}
      <div className={S.tabsHeader}>
        <button
          className={`${S.tab} ${activeTab === FILTER_TABS.ALL ? S.active : ""}`}
          onClick={() => setActiveTab(FILTER_TABS.ALL)}
        >
          ALL <span className={S.tabCount}>{tabCounts.all}</span>
        </button>
        <button
          className={`${S.tab} ${activeTab === FILTER_TABS.PATHS ? S.active : ""}`}
          onClick={() => setActiveTab(FILTER_TABS.PATHS)}
        >
          PATHS <span className={S.tabCount}>{tabCounts.paths}</span>
        </button>
        <button
          className={`${S.tab} ${activeTab === FILTER_TABS.LESSONS ? S.active : ""}`}
          onClick={() => setActiveTab(FILTER_TABS.LESSONS)}
        >
          LESSONS <span className={S.tabCount}>{tabCounts.lessons}</span>
        </button>
      </div>

      {/* Results Grid */}
      <div className={S.resultsGrid}>
        {filteredResults.map((item, index) => (
          <ContentCard key={item.collectionKey || index} {...item} />
        ))}
      </div>

      {/* Empty filtered state */}
      {filteredResults.length === 0 && (
        <div className={S.emptyFilterState}>
          <p>No {activeTab} found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default CatalogResults;
